import { Checkbox, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import { IPrikazZmenit, PrikazZmenitGet, PrikazZmenitPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import emailValidation from 'utils/validations/emailValidation';
import pscValidation from 'utils/validations/pscValidation';

interface IPrikazZmena {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
    // prikazyIsins: IPrikazyIsins;
}

const PrikazZmena: React.FC<IPrikazZmena> = ({ nextStep, zadostID, prikazyData }) => {
    const [form] = Form.useForm();
    const { cisStaty } = useContext(Context);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazZmenit>();

    useEffect(() => {
        setLoading(true);
        PrikazZmenitGet(zadostID)
            .then(resp => {
                setData(resp);
                form.setFieldsValue({
                    znazev: resp.znazev,
                    zadresa: resp.zadresa,
                    zemailfakt: resp.zemailfakt,
                    zemailpopl: resp.zemailpopl,
                    zjina: resp.zjina
                });
            })
            .catch(() => setData({} as IPrikazZmenit))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            const dataToSend = {
                                ...(values as any),
                                datum: values.datum.format('YYYY-MM-DD')
                            };
                            delete dataToSend.idmbSpolecnost;
                            delete dataToSend.dicdphSpolecnost;
                            delete dataToSend.nazevSpolecnost;
                            delete dataToSend.leiSpolecnost;
                            PrikazZmenitPut(zadostID, dataToSend)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            // disabledDate={current => current < dayjs().add(4, 'days')}
                            label="PRIKAZ_PREVOD_DATUM"
                        />
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="znazev"
                                label={<TranslateText>PRIKAZ_DRUH_ZMENY_ZNAZEV</TranslateText>}
                                initialValue={data?.znazev || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => rerender(pv => !pv)}
                                />
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="zadresa"
                                label={<TranslateText>PRIKAZ_DRUH_ZMENY_ZADRESA</TranslateText>}
                                initialValue={data?.zadresa || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => rerender(pv => !pv)}
                                />
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="zemailfakt"
                                label={<TranslateText>PRIKAZ_DRUH_ZMENY_ZEMAILFAKT</TranslateText>}
                                initialValue={data?.zemailfakt || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => rerender(pv => !pv)}
                                />
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="zemailpopl"
                                label={<TranslateText>PRIKAZ_DRUH_ZMENY_ZEMAILPOPL</TranslateText>}
                                initialValue={data?.zemailpopl || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => rerender(pv => !pv)}
                                />
                            </Form.Item>
                        </Row>
                        {/* <Row>
                        <Form.Item
                            style={{
                                fontWeight: 600
                            }}
                            onChange={() => rerender(pv => !pv)}
                            name="zadmin"
                            label={<TranslateText>PRIKAZ_DRUH_ZMENY_ZADMIN</TranslateText>}
                            initialValue={data?.zadmin || false}
                            valuePropName="checked"
                        >
                            <Checkbox
                                style={{
                                    position: 'relative',
                                    top: -2,
                                    left: 4
                                }}
                            />
                        </Form.Item>
                    </Row>*/}
                        <Row>
                            <Form.Item
                                style={{
                                    fontWeight: 600
                                }}
                                name="zjina"
                                label={<TranslateText>PRIKAZ_DRUH_ZMENY_ZJINA</TranslateText>}
                                initialValue={data?.zjina || false}
                                valuePropName="checked"
                            >
                                <Checkbox
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4
                                    }}
                                    onChange={() => rerender(pv => !pv)}
                                />
                            </Form.Item>
                        </Row>
                        {form.getFieldValue('znazev') && (
                            <>
                                <Title level={4} text="PRIKAZ_DRUH_ZMENY_ZNAZEV" dividerTitle={true} />
                                <InputComponent
                                    initialValue={prikazyData?.spolecnost.nazev || data?.nazevp}
                                    disabled={!!prikazyData?.spolecnost.nazev}
                                    label="PUVODNI_UDAJ"
                                    name="nazevp"
                                />
                                <InputComponent initialValue={data?.nazevn} label="NOVY_UDAJ" name="nazevn" />
                            </>
                        )}
                        {form.getFieldValue('zadresa') && (
                            <>
                                <Title level={4} text="PRIKAZ_DRUH_ZMENY_ZADRESA" dividerTitle={true} />
                                <Title level={5} text="PUVODNI_UDAJE" />
                                <InputComponent
                                    label="ULICE_CP"
                                    name="admap"
                                    initialValue={prikazyData?.spolecnost.adma || data.admap}
                                    disabled={!!prikazyData?.spolecnost.adma}
                                />
                                <InputComponent
                                    label="MESTO"
                                    name="memap"
                                    initialValue={prikazyData?.spolecnost.mema || data.memap}
                                    disabled={!!prikazyData?.spolecnost.mema}
                                />
                                <InputComponent
                                    label="PSC"
                                    name="pscmp"
                                    initialValue={prikazyData?.spolecnost.pscm || data.pscmp}
                                    disabled={!!prikazyData?.spolecnost.pscm}
                                    usePscFormater={
                                        form.getFieldValue('stat') === 'SK' || form.getFieldValue('stat') === 'CZ'
                                    }
                                    rules={pscValidation('kostn')}
                                    maxLength={10}
                                />
                                <SelectComponent
                                    name="kostp"
                                    labelParagraph="STAT_SIDLA"
                                    isFormItem={true}
                                    data={cisStaty}
                                    defaultValue={prikazyData?.spolecnost.kost || data.kostp}
                                    disabled={!!prikazyData?.spolecnost.kost}
                                />
                                <Title level={5} text="NOVY_UDAJE" />
                                <InputComponent label="ULICE_CP" name="adman" initialValue={data?.adman} />
                                <InputComponent label="MESTO" name="meman" initialValue={data?.meman} />
                                <InputComponent
                                    label="PSC"
                                    name="pscmn"
                                    initialValue={data?.pscmn}
                                    maxLength={10}
                                    usePscFormater={
                                        form.getFieldValue('kostn') === 'SK' || form.getFieldValue('kostn') === 'CZ'
                                    }
                                    rules={pscValidation('kostn')}
                                />
                                <SelectComponent
                                    name="kostn"
                                    labelParagraph="STAT_SIDLA"
                                    isFormItem={true}
                                    data={cisStaty}
                                    defaultValue={data?.kostn}
                                />
                            </>
                        )}
                        {form.getFieldValue('zemailfakt') && (
                            <>
                                <Title level={4} text="PRIKAZ_DRUH_ZMENY_ZEMAILFAKT" dividerTitle={true} />
                                <InputComponent
                                    initialValue={data?.emailfaktp}
                                    label="PUVODNI_UDAJ"
                                    name="emailfaktp"
                                    notRequired={true}
                                    rules={emailValidation()}
                                />
                                <InputComponent
                                    initialValue={data?.emailfaktn}
                                    label="NOVY_UDAJ"
                                    name="emailfaktn"
                                    rules={emailValidation()}
                                />
                            </>
                        )}
                        {form.getFieldValue('zemailpopl') && (
                            <>
                                <Title level={4} dividerTitle={true} text="PRIKAZ_DRUH_ZMENY_ZEMAILPOPL" />
                                <InputComponent
                                    initialValue={data?.emailpoplp}
                                    label="PUVODNI_UDAJ"
                                    name="emailpoplp"
                                    notRequired={true}
                                    rules={emailValidation()}
                                />
                                <InputComponent
                                    initialValue={data?.emailpopln}
                                    label="NOVY_UDAJ"
                                    name="emailpopln"
                                    rules={emailValidation()}
                                />
                            </>
                        )}
                        {/* form.getFieldValue('zadmin') && (
                        <>
                            <Title level={4} text="NOVY_ADMIN" />
                            <Title level={4} text="DODATEK_OSOBY_SUBTITLE" />
                            <Title level={5} text="DODATEK_OSOBY_SUBTITLE_IDENTIFIKACE" dividerTitle={true} />
                            <Row gutter={12}>
                                <Col span={21}>
                                    <InputComponent
                                        label="ICO"
                                        name="idmb"
                                        initialValue={data?.idmb}
                                        type="text"
                                        rules={icoValidation()}
                                        maxLength={10}
                                    />
                                </Col>
                                <Col
                                    span={3}
                                    style={{
                                        paddingTop: '35px'
                                    }}
                                >
                                    <ButtonComponent
                                        id="zpetBtn"
                                        buttontext="VYHLEDAT"
                                        htmlType="button"
                                        onClick={() => {
                                            if (form.getFieldValue('idmb')) {
                                                setLoading(true);
                                                leiRejstrkApi({
                                                    ic: form.getFieldValue('idmb'),
                                                    stat: 'CZ',
                                                    lang: language,
                                                    regauthor: 'RA000163'
                                                })
                                                    .then(resp => {
                                                        setSpolecnostResp(resp);
                                                        form.setFieldsValue({
                                                            nazev: resp.legalname,
                                                            // eslint-disable-next-line max-len
                                                            adma: `${resp.adresy[0]?.firstaddressline}, ${resp.adresy[0]?.city}, ${resp.adresy[0]?.postalcode}, ${getCountryText(
                                                                resp.adresy[0]?.country || '',
                                                                language
                                                            )}`
                                                        });
                                                    })
                                                    .finally(() => setLoading(false));
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                            <InputComponent
                                label="NAZEV_ZASTUPCE"
                                name="nazev"
                                type="text"
                                initialValue={data?.nazev}
                            />
                            <InputComponent
                                label="ADRESA_ZASTUPCE"
                                name="adma"
                                type="text"
                                initialValue={
                                    data?.adma
                                        ? // eslint-disable-next-line max-len
                                          `${data?.adma}, ${data?.mema}, ${data?.pscm}, ${getCountryText(
                                              data?.kost || '',
                                              language
                                          )}`
                                        : ''
                                }
                            />
                            <Title level={5} text="IDENTIFIKACE_ZASTUPCE" dividerTitle={true} />
                            <InputComponent
                                label="FORMULARE_JMENA"
                                name="pjma"
                                type="text"
                                maxLength={200}
                                initialValue={data?.pjma}
                            />
                            <InputComponent
                                label="FORMULARE_PRIJMENI"
                                name="jmma"
                                initialValue={data?.jmma}
                                type="text"
                                maxLength={200}
                            />
                            <InputComponent
                                label="EMAIL"
                                name="email"
                                type="text"
                                maxLength={200}
                                initialValue={data?.email}
                                rules={emailValidation()}
                            />
                            <PhoneNumberInput
                                label="TELEFON"
                                name="telefon"
                                initialValue={data?.telefon}
                                maxLength={10}
                            />
                            <SelectComponent
                                labelParagraph="AUTENTIZACE_ZASTUPCE"
                                name="typautn"
                                data={[
                                    {
                                        labelcz: 'PXKLIC',
                                        value: 'PXKLIC'
                                    },
                                    {
                                        labelcz: 'CERTIFIKAT',
                                        value: 'CERT'
                                    }
                                ]}
                                onChange={() => rerender(pv => !pv)}
                                defaultValue={data?.typautn}
                                isFormItem={true}
                            />
                            {form.getFieldValue('typaut') === 'CERT' && (
                                <>
                                    <InputComponent
                                        label="IDENTIFIKACE_ZASTUPCE_CERT"
                                        initialValue={data?.cersn}
                                        name="cersn"
                                        type="text"
                                        // disabled={true}
                                    />
                                    <InputComponent
                                        label="IDENTIFIKACE_ZASTUPCE_CERTISS"
                                        name="ceriss"
                                        type="text"
                                        // disabled={true}
                                        initialValue={data?.ceriss}
                                    />
                                </>
                            )}
                            <Paragraph
                                style={{
                                    display: 'block',
                                    margin: '10px 0px'
                                }}
                                text={'AUTENTIZACE_ZASTUPCE_TEXT'}
                            />
                            <Paragraph
                                style={{
                                    display: 'block',
                                    margin: '10px 0px'
                                }}
                                text={'AUTENTIZACE_ZASTUPCE_TEXT1'}
                            />
                            <Paragraph
                                style={{
                                    display: 'block',
                                    margin: '10px 0px'
                                }}
                                text={'AUTENTIZACE_ZASTUPCE_TEXT2'}
                            />
                        </>
                     )*/}
                        {form.getFieldValue('zjina') && (
                            <>
                                <Title level={4} dividerTitle={true} text="PRIKAZ_DRUH_ZMENY_ZJINA" />
                                <InputTextArea
                                    rows={4}
                                    name="popis"
                                    initialValue={data?.popis}
                                    label="ZMENA_JINA_POPIS"
                                />
                            </>
                        )}
                        <Title level={4} text="PRIKAZ_DRUH_ZMENY" />
                        <PrilohyTable zadostID={zadostID} />
                        <Paragraph
                            text={'PRIKAZ_ZMENA_DLT_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={'PRIKAZ_ZMENA_DLT_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
        </LoadingWrapper>
    );
};

export default PrikazZmena;
